/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {GetterTree} from 'vuex';
import {RootState} from '@/store/types';
import {
    ActiveCommunityChallengeStateInterface, CommunityChallengeAchievementInterface,
    CommunityChallengeInterface, CommunityChallengeMilestoneInterface,
} from '@/store/modules/community-challenge/types';

/**
 * The Community Challenge module getters provide entry points for stored data.
 * @author Mark Thompson <mthompson@frontier.co.uk>
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const getters: GetterTree<ActiveCommunityChallengeStateInterface, RootState> = {

    /**
     * Get the Current Challenge Info
     * @param {ActiveCommunityChallengeStateInterface} state
     * @returns {CommunityChallengeInterface}
     */
    challenge(state: ActiveCommunityChallengeStateInterface): CommunityChallengeInterface {
        return state.challenge;
    },

    /**
     * Get the user contribution to the current challenge
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {number}
     */
    count(state: ActiveCommunityChallengeStateInterface): number {
        return state.count;
    },

    /**
     * Get the filtered challenge title
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {number}
     */
    title(state: ActiveCommunityChallengeStateInterface): string {
        if (!state.challenge) {
            return '';
        }

        return (state.challenge.title || '').replace('{count}', `${state.challenge.quantity}`);
    },

    /**
     * Get the filtered challenge synopsis
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {number}
     */
    synopsis(state: ActiveCommunityChallengeStateInterface): string {
        if (!state.challenge) {
            return '';
        }

        return (state.challenge.synopsis || '').replace('{count}', `${state.challenge.quantity}`);
    },

    /**
     * Get the filtered challenge description
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {number}
     */
    description(state: ActiveCommunityChallengeStateInterface): string {
        if (!state.challenge) {
            return '';
        }

        return (state.challenge.description || '').replace('{count}', `${state.challenge.quantity}`);
    },

    /**
     * Get the user achievements for the current challenge.
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {CommunityChallengeAchievementInterface[]}
     */
    achievements(state: ActiveCommunityChallengeStateInterface): CommunityChallengeAchievementInterface[] {
        return state.achievements;
    },

    /**
     * Get the milestones for the current challenge.
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {CommunityChallengeMilestoneInterface[]}
     */
    milestones(state: ActiveCommunityChallengeStateInterface): CommunityChallengeMilestoneInterface[] {
        return state.milestones;
    },

    /**
     * Get the milestones for the current challenge.
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {CommunityChallengeMilestoneInterface[]}
     */
    completionMilestones(state: ActiveCommunityChallengeStateInterface): CommunityChallengeMilestoneInterface[] {
        return state.milestones.filter((milestone: CommunityChallengeMilestoneInterface) => {
            return 'completion' === milestone.type;
        });
    },

    /**
     * Get the milestones for the current challenge.
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {CommunityChallengeMilestoneInterface[]}
     */
    personalMilestones(state: ActiveCommunityChallengeStateInterface): CommunityChallengeMilestoneInterface[] {
        return state.milestones.filter((milestone: CommunityChallengeMilestoneInterface) => {
            return 'personal' === milestone.type;
        });
    },

    /**
     * Check for unclaimed achievements for the current challenge.
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {CommunityChallengeMilestoneInterface[]}
     */
    isClaimable(state: ActiveCommunityChallengeStateInterface): boolean {
        return state.claimable.reduce((accumulator: boolean, item: CommunityChallengeAchievementInterface) => {
            return accumulator || item.claimable;
        }, false);
    },

    /**
     *
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {CommunityChallengeAchievementInterface[]}
     */
    claimable(state: ActiveCommunityChallengeStateInterface): CommunityChallengeAchievementInterface[] {
        return state.claimable;
    },

    /**
     * Get the unclaimed achievements for the current challenge.
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {CommunityChallengeMilestoneInterface[]}
     */
    unclaimed(state: ActiveCommunityChallengeStateInterface): number {
        return state.claimable.reduce((accumulator: number, item: CommunityChallengeAchievementInterface) => {
            return accumulator + (item.claimable ? 1 : 0);
        }, 0);
    },

    /**
     * Get the claimed achievements for the current challenge.
     * @param {ActiveCommunityChallengeStateInterface} state
     * @return {CommunityChallengeMilestoneInterface[]}
     */
    claimed(state: ActiveCommunityChallengeStateInterface): number {
        return state.achievements.reduce((accumulator: number, item: CommunityChallengeAchievementInterface) => {
            return accumulator + (item.claimable ? 0 : 1);
        }, 0);
    },
};
